"use strict";
/**
 * Supported countries for an investor's payout bank account and the fields each collects.
 * The web form, the validators and the payout paths all read this table so hints, required
 * fields and patterns cannot drift between layers.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.summariseInvestorBankRouting = exports.formatInvestorBankDetailRows = exports.getMissingInvestorBankFields = exports.isManualPaymentBankDetail = exports.isAbaEligibleBankDetail = exports.hasPayableBankDetail = exports.validateInvestorBankDetail = exports.normaliseInvestorBankDetail = exports.effectiveBankCountry = exports.getInvestorBankCountry = exports.isSupportedInvestorBankCountry = exports.INVESTOR_BANK_FIELDS = exports.INVESTOR_BANK_COUNTRIES = exports.DEFAULT_INVESTOR_BANK_COUNTRY = void 0;
exports.DEFAULT_INVESTOR_BANK_COUNTRY = "AUS";
const SWIFT_PATTERN = /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/;
const SWIFT_MESSAGE = "SWIFT/BIC must be 8 or 11 letters and digits";
const accountName = (required) => ({
    field: "bankAccountName",
    label: "Account name",
    hint: "As it appears on the bank statement",
    placeholder: "Account name",
    required,
});
const bankName = (example) => ({
    field: "bankName",
    label: "Bank name",
    hint: `For example ${example}`,
    placeholder: "Bank name",
    required: true,
});
const swiftBic = (hint) => ({
    field: "swiftBic",
    label: "SWIFT/BIC",
    hint,
    placeholder: "SWIFT/BIC",
    required: true,
    pattern: SWIFT_PATTERN,
    patternMessage: SWIFT_MESSAGE,
});
exports.INVESTOR_BANK_COUNTRIES = [
    {
        code: "AUS",
        name: "Australia",
        abaEligible: true,
        fields: [
            {
                field: "bankBSB",
                label: "BSB",
                hint: "6 digits, for example 123-456",
                placeholder: "BSB (e.g. 123-456)",
                required: false,
                pattern: /^\d{3}-\d{3}$/,
                patternMessage: "BSB must be in the format 000-000",
            },
            {
                field: "bankAccountNumber",
                label: "Account number",
                hint: "6 to 9 digits",
                placeholder: "Account number",
                required: false,
                pattern: /^\d{6,9}$/,
                patternMessage: "Account number must be 6–9 digits",
                sensitive: true,
            },
            accountName(false),
        ],
    },
    {
        code: "NZL",
        name: "New Zealand",
        abaEligible: false,
        fields: [
            accountName(true),
            bankName("ANZ Bank New Zealand"),
            {
                field: "bankAccountNumber",
                label: "Account number",
                hint: "Bank, branch, account and suffix, for example 12-3456-7890123-00",
                placeholder: "00-0000-0000000-00",
                required: true,
                pattern: /^\d{2}-?\d{4}-?\d{7}-?\d{2,3}$/,
                patternMessage: "Account number must be bank, branch, account and suffix (15 or 16 digits)",
                sensitive: true,
            },
            swiftBic("8 or 11 characters, for example ANZBNZ22"),
        ],
    },
    {
        code: "GBR",
        name: "United Kingdom",
        abaEligible: false,
        fields: [
            accountName(true),
            bankName("Barclays Bank UK"),
            {
                field: "routingCode",
                label: "Sort code",
                hint: "6 digits, for example 12-34-56",
                placeholder: "00-00-00",
                required: true,
                pattern: /^\d{2}-?\d{2}-?\d{2}$/,
                patternMessage: "Sort code must be 6 digits",
            },
            {
                field: "bankAccountNumber",
                label: "Account number",
                hint: "8 digits",
                placeholder: "Account number",
                required: true,
                pattern: /^\d{8}$/,
                patternMessage: "Account number must be 8 digits",
                sensitive: true,
            },
            {
                field: "iban",
                label: "IBAN",
                hint: "Optional. Starts with GB, 22 characters",
                placeholder: "GB00 XXXX 0000 0000 0000 00",
                required: false,
                pattern: /^GB\d{2}[A-Z]{4}\d{14}$/,
                patternMessage: "IBAN must start with GB and be 22 characters",
                sensitive: true,
            },
            swiftBic("8 or 11 characters, for example BARCGB22"),
        ],
    },
    {
        code: "USA",
        name: "United States",
        abaEligible: false,
        fields: [
            accountName(true),
            bankName("Bank of America"),
            {
                field: "routingCode",
                label: "ABA routing number",
                hint: "9 digits, printed at the bottom of a cheque",
                placeholder: "000000000",
                required: true,
                pattern: /^\d{9}$/,
                patternMessage: "ABA routing number must be 9 digits",
            },
            {
                field: "bankAccountNumber",
                label: "Account number",
                hint: "4 to 17 digits",
                placeholder: "Account number",
                required: true,
                pattern: /^\d{4,17}$/,
                patternMessage: "Account number must be 4–17 digits",
                sensitive: true,
            },
            swiftBic("Needed to receive a payment from Australia, for example BOFAUS3N"),
        ],
    },
    {
        code: "SGP",
        name: "Singapore",
        abaEligible: false,
        fields: [
            accountName(true),
            bankName("DBS Bank"),
            {
                field: "routingCode",
                label: "Bank code",
                hint: "4 digits, for example 7171",
                placeholder: "0000",
                required: true,
                pattern: /^\d{4}$/,
                patternMessage: "Bank code must be 4 digits",
            },
            {
                field: "bankAccountNumber",
                label: "Account number",
                hint: "7 to 14 digits",
                placeholder: "Account number",
                required: true,
                pattern: /^\d{7,14}$/,
                patternMessage: "Account number must be 7–14 digits",
                sensitive: true,
            },
            swiftBic("8 or 11 characters, for example DBSSSGSG"),
        ],
    },
];
exports.INVESTOR_BANK_FIELDS = [
    "bankAccountName",
    "bankName",
    "bankBSB",
    "routingCode",
    "bankAccountNumber",
    "iban",
    "swiftBic",
];
const COMPACTED_FIELDS = new Set(["iban", "swiftBic"]);
const isSupportedInvestorBankCountry = (code) => exports.INVESTOR_BANK_COUNTRIES.some((country) => country.code === code?.trim().toUpperCase());
exports.isSupportedInvestorBankCountry = isSupportedInvestorBankCountry;
const getInvestorBankCountry = (code) => exports.INVESTOR_BANK_COUNTRIES.find((country) => country.code === (code?.trim().toUpperCase() || exports.DEFAULT_INVESTOR_BANK_COUNTRY));
exports.getInvestorBankCountry = getInvestorBankCountry;
const effectiveBankCountry = (detail) => detail?.country?.trim().toUpperCase() || exports.DEFAULT_INVESTOR_BANK_COUNTRY;
exports.effectiveBankCountry = effectiveBankCountry;
const cleanValue = (field, value) => {
    if (typeof value !== "string")
        return undefined;
    const trimmed = value.trim();
    if (!trimmed)
        return undefined;
    return COMPACTED_FIELDS.has(field) ? trimmed.replace(/\s+/g, "").toUpperCase() : trimmed;
};
/**
 * Trims every value, compacts SWIFT/IBAN, and drops fields that do not belong to the account's
 * country so a stale BSB can never sit under an overseas account. `country` is kept only when the
 * caller supplied it, so an Australian save without a country stays shaped as it always was.
 */
const normaliseInvestorBankDetail = (detail) => {
    if (!detail)
        return undefined;
    const country = (0, exports.getInvestorBankCountry)((0, exports.effectiveBankCountry)(detail));
    const allowed = new Set(country ? country.fields.map((rule) => rule.field) : exports.INVESTOR_BANK_FIELDS);
    const result = {};
    if (detail.country?.trim())
        result.country = detail.country.trim().toUpperCase();
    exports.INVESTOR_BANK_FIELDS.forEach((field) => {
        if (!allowed.has(field))
            return;
        const value = cleanValue(field, detail[field]);
        if (value !== undefined)
            result[field] = value;
    });
    return Object.keys(result).length > 0 ? result : undefined;
};
exports.normaliseInvestorBankDetail = normaliseInvestorBankDetail;
/** Field → message for every required field missing or pattern failed. Empty when valid. */
const validateInvestorBankDetail = (detail) => {
    const errors = {};
    if (!detail)
        return errors;
    const country = (0, exports.getInvestorBankCountry)((0, exports.effectiveBankCountry)(detail));
    if (!country) {
        errors.country = "Bank account country is not supported";
        return errors;
    }
    country.fields.forEach((rule) => {
        const value = cleanValue(rule.field, detail[rule.field]);
        if (!value) {
            if (rule.required)
                errors[rule.field] = `${rule.label} is required`;
            return;
        }
        if (rule.pattern && !rule.pattern.test(value)) {
            errors[rule.field] = rule.patternMessage ?? `${rule.label} is invalid`;
        }
    });
    return errors;
};
exports.validateInvestorBankDetail = validateInvestorBankDetail;
const hasValue = (detail, field) => Boolean(detail[field]?.trim());
/**
 * Whether the account is complete enough to pay. Australia keeps the long-standing rule (BSB and
 * account number; the ABA builder substitutes a name); other countries need every required field.
 */
const hasPayableBankDetail = (detail) => {
    if (!detail)
        return false;
    const country = (0, exports.getInvestorBankCountry)((0, exports.effectiveBankCountry)(detail));
    if (!country)
        return false;
    if (country.abaEligible)
        return hasValue(detail, "bankBSB") && hasValue(detail, "bankAccountNumber");
    return country.fields.every((rule) => !rule.required || hasValue(detail, rule.field));
};
exports.hasPayableBankDetail = hasPayableBankDetail;
const isAbaEligibleBankDetail = (detail) => Boolean(detail) &&
    (0, exports.getInvestorBankCountry)((0, exports.effectiveBankCountry)(detail))?.abaEligible === true &&
    (0, exports.hasPayableBankDetail)(detail);
exports.isAbaEligibleBankDetail = isAbaEligibleBankDetail;
const isManualPaymentBankDetail = (detail) => (0, exports.hasPayableBankDetail)(detail) && !(0, exports.isAbaEligibleBankDetail)(detail);
exports.isManualPaymentBankDetail = isManualPaymentBankDetail;
/** Labels of fields still missing. Australia lists all three fields, as the warning banner always has. */
const getMissingInvestorBankFields = (detail) => {
    const country = (0, exports.getInvestorBankCountry)((0, exports.effectiveBankCountry)(detail));
    if (!country)
        return [];
    return country.fields
        .filter((rule) => (country.abaEligible || rule.required) && !(detail && hasValue(detail, rule.field)))
        .map((rule) => rule.label);
};
exports.getMissingInvestorBankFields = getMissingInvestorBankFields;
/** Display rows in the country's field order. Australia omits the country row, as screens always have. */
const formatInvestorBankDetailRows = (detail, options = {}) => {
    const code = (0, exports.effectiveBankCountry)(detail);
    const country = (0, exports.getInvestorBankCountry)(code);
    const rows = [];
    if (options.includeCountry ?? !country?.abaEligible) {
        rows.push({ field: "country", label: "Country", value: country?.name ?? code });
    }
    (country?.fields ?? []).forEach((rule) => {
        rows.push({ field: rule.field, label: rule.label, value: detail?.[rule.field]?.trim() || "-" });
    });
    return rows;
};
exports.formatInvestorBankDetailRows = formatInvestorBankDetailRows;
/** One-line routing text for the `BSB / routing` row on statements and notices. */
const summariseInvestorBankRouting = (detail) => {
    const country = (0, exports.getInvestorBankCountry)((0, exports.effectiveBankCountry)(detail));
    if (!detail || !country)
        return "—";
    if (country.abaEligible)
        return detail.bankBSB?.trim() || "—";
    const parts = country.fields
        .filter((rule) => rule.field === "routingCode" || rule.field === "swiftBic")
        .filter((rule) => hasValue(detail, rule.field))
        .map((rule) => `${rule.label} ${detail[rule.field]?.trim()}`);
    return parts.length > 0 ? `${parts.join(" · ")} (${country.name})` : country.name;
};
exports.summariseInvestorBankRouting = summariseInvestorBankRouting;
//# sourceMappingURL=investorBankAccounts.js.map